import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { PosService } from "../../services";
import { useAuth } from "../../store/AuthContext";
import { currency, dateTime } from "../../utils/format";
import { currentBusinessDate, isSalesBlockedNow } from "../../utils/businessDay";
import { getSaleItemTypeLabel, isLiquorSaleItem } from "../../utils/barProducts";
import {
  Eye,
  Loader2,
  ReceiptText,
  RefreshCw,
  Search,
  ShoppingCart,
  X,
  Info,
} from "lucide-react";

const PAGE_FETCH_SIZE = 1000;

const BOTTLE_SALE_MODES = {
  FULL_BOTTLE: "FULL_BOTTLE",
  MEASURE_180ML: "MEASURE_180ML",
  MEASURE_90ML: "MEASURE_90ML",
};

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

const getNumberValue = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== "") {
      const numberValue = Number(value);

      if (Number.isFinite(numberValue)) {
        return numberValue;
      }
    }
  }

  return 0;
};

const getPaymentTotal = (sale, paymentMethod) =>
  (sale?.payments || [])
    .filter((payment) => payment.paymentMethod === paymentMethod)
    .reduce((sum, payment) => sum + getNumberValue(payment.amount), 0);

const getBottleTotal = (sale) =>
  getNumberValue(sale?.totalBottleAmount, sale?.bottleSalesAmount, sale?.bottleTotal);

const getRestaurantTotal = (sale) =>
  getNumberValue(
    sale?.totalBiteAmount,
    sale?.restaurantSalesAmount,
    sale?.biteSalesAmount,
    sale?.restaurantTotal
  );

const getSubtotal = (sale) =>
  getNumberValue(sale?.subtotalAmount, sale?.subTotal);

const isSaleSplitPayment = (sale) =>
  getPaymentTotal(sale, "CASH") > 0 && getPaymentTotal(sale, "CARD") > 0;

const calculateSalePaymentDivision = (sale) => {
  const bottleTotal = getBottleTotal(sale);
  const restaurantTotal = getRestaurantTotal(sale);
  const subtotal = getSubtotal(sale);
  const cashAmount = getPaymentTotal(sale, "CASH");
  const cardAmount = getPaymentTotal(sale, "CARD");

  const barRatio = subtotal > 0 ? bottleTotal / subtotal : 0;
  const restaurantRatio = subtotal > 0 ? restaurantTotal / subtotal : 0;

  return {
    bottleTotal: roundMoney(bottleTotal),
    restaurantTotal: roundMoney(restaurantTotal),
    subtotal: roundMoney(subtotal),
    cashAmount: roundMoney(cashAmount),
    cardAmount: roundMoney(cardAmount),
    barRatio,
    restaurantRatio,
    barCash: roundMoney(cashAmount * barRatio),
    restaurantCash: roundMoney(cashAmount * restaurantRatio),
    barCard: roundMoney(cardAmount * barRatio),
    restaurantCard: roundMoney(cardAmount * restaurantRatio),
  };
};

const formatRatioPercent = (ratio) => `${(Number(ratio || 0) * 100).toFixed(2)}%`;

export const MySalesPage = ({
  posPath = "/cashier/bar-sales",
  saleDetailPath = (saleId) => `/cashier/sales/${saleId}`,
  paginationStorageKey = "cashier-my-sales",
} = {}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const today = currentBusinessDate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salesBlocked, setSalesBlocked] = useState(isSalesBlockedNow());
  const [billSearch, setBillSearch] = useState("");

  const [summarySale, setSummarySale] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const [filterMode, setFilterMode] = useState(
    searchParams.get("mode") || "single"
  );

  const [businessDate, setBusinessDate] = useState(
    searchParams.get("businessDate") || today
  );

  const [fromDate, setFromDate] = useState(
    searchParams.get("fromDate") || today
  );

  const [toDate, setToDate] = useState(searchParams.get("toDate") || today);

  const didLoadRef = useRef(false);

  const loadMySales = async ({
    mode = filterMode,
    selectedBusinessDate = businessDate,
    selectedFromDate = fromDate,
    selectedToDate = toDate,
  } = {}) => {
    try {
      setLoading(true);

      const requestFromDate =
        mode === "single" ? selectedBusinessDate : selectedFromDate;

      const requestToDate =
        mode === "single" ? selectedBusinessDate : selectedToDate;

      const result = await PosService.mySalesPage({
        page: 0,
        size: PAGE_FETCH_SIZE,
        fromDate: requestFromDate,
        toDate: requestToDate,
      });

      const pageData = result?.data || result;

      const content = Array.isArray(pageData?.content)
        ? pageData.content
        : Array.isArray(pageData)
          ? pageData
          : [];

      setRows(content);
    } catch (error) {
      console.error("Failed to load my sales", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;

    loadMySales();
  }, [user.userId]);

  useEffect(() => {
    const checkBlocked = () => setSalesBlocked(isSalesBlockedNow());

    checkBlocked();

    const interval = setInterval(checkBlocked, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleModeChange = (mode) => {
    setFilterMode(mode);

    if (mode === "single" && !businessDate) {
      setBusinessDate(today);
    }

    if (mode === "range") {
      if (!fromDate) {
        setFromDate(today);
      }

      if (!toDate) {
        setToDate(today);
      }
    }
  };

  const handleRefresh = () => {
    if (filterMode === "range" && fromDate && toDate && fromDate > toDate) {
      alert("From date cannot be after To date");
      return;
    }

    const nextBusinessDate = businessDate || today;
    const nextFromDate = fromDate || today;
    const nextToDate = toDate || nextFromDate;

    const params = {
      mode: filterMode,
    };

    if (filterMode === "single") {
      params.businessDate = nextBusinessDate;
    } else {
      params.fromDate = nextFromDate;
      params.toDate = nextToDate;
    }

    setSearchParams(params);

    loadMySales({
      mode: filterMode,
      selectedBusinessDate: nextBusinessDate,
      selectedFromDate: nextFromDate,
      selectedToDate: nextToDate,
    });
  };

  const filteredRows = useMemo(() => {
    const q = billSearch.trim().toLowerCase();

    if (!q) {
      return rows;
    }

    return rows.filter((row) => {
      const saleCode = row.saleCode || "";
      const billNumber = row.billNumber || "";

      return (
        saleCode.toLowerCase().includes(q) ||
        billNumber.toLowerCase().includes(q)
      );
    });
  }, [rows, billSearch]);

  const currentQueryString = searchParams.toString();

  const openSummary = async (saleRow) => {
    setSummarySale(saleRow);
    setSummaryError(null);
    setSummaryLoading(true);

    try {
      const saleDetail = await PosService.saleDetail(saleRow.saleId);
      setSummarySale(saleDetail);
    } catch (error) {
      console.error("Failed to load sale summary", error);
      setSummaryError(
        error?.response?.data?.message || "Failed to load sale summary"
      );
    } finally {
      setSummaryLoading(false);
    }
  };

  const closeSummary = () => {
    setSummarySale(null);
    setSummaryError(null);
    setSummaryLoading(false);
  };

  return (
    <div>
      <PageHeader
        title="My Sales"
        subtitle="Your recent bills. Tap a bill to view or request correction."
        actions={
          salesBlocked ? (
            <button
              disabled
              className="px-5 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 bg-slate-200 text-slate-500 cursor-not-allowed"
              title="Sales are blocked after 2:10 AM. Please close your cash session."
            >
              <ShoppingCart className="h-4 w-4" /> Sales Closed
            </button>
          ) : (
            <Link
              to={posPath}
              className="bms-btn-accent px-5 py-2.5 rounded-lg text-sm inline-flex items-center gap-2"
            >
              <ShoppingCart className="h-4 w-4" /> New Sale
            </Link>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />

          <input
            type="text"
            value={billSearch}
            onChange={(event) => setBillSearch(event.target.value)}
            placeholder="Search bill no..."
            className="w-full border border-slate-200 bg-white rounded-xl pl-9 pr-4 py-2 text-sm shadow-sm"
          />
        </div>

        <div className="flex flex-wrap items-end justify-end gap-3">
          <div className="rounded-xl bg-slate-100 p-1 flex">
            <button
              type="button"
              onClick={() => handleModeChange("single")}
              className={`px-4 py-2 rounded-lg text-sm ${
                filterMode === "single"
                  ? "bg-white text-navy-800 shadow-sm"
                  : "text-slate-600"
              }`}
            >
              Single day
            </button>

            <button
              type="button"
              onClick={() => handleModeChange("range")}
              className={`px-4 py-2 rounded-lg text-sm ${
                filterMode === "range"
                  ? "bg-white text-navy-800 shadow-sm"
                  : "text-slate-600"
              }`}
            >
              Date range
            </button>
          </div>

          {filterMode === "single" ? (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Business date
              </label>

              <input
                type="date"
                value={businessDate}
                onChange={(event) => setBusinessDate(event.target.value)}
                className="border border-slate-200 bg-white rounded-xl px-4 py-2 text-sm shadow-sm"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  From date
                </label>

                <input
                  type="date"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                  className="border border-slate-200 bg-white rounded-xl px-4 py-2 text-sm shadow-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  To date
                </label>

                <input
                  type="date"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                  className="border border-slate-200 bg-white rounded-xl px-4 py-2 text-sm shadow-sm"
                />
              </div>
            </>
          )}

          <button
            type="button"
            onClick={handleRefresh}
            className="px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2 bg-navy-700 text-white hover:bg-navy-800"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      <DataTable
        paginationStorageKey={paginationStorageKey}
        loading={loading}
        rows={(Array.isArray(filteredRows) ? filteredRows : []).map((r) => ({
          ...r,
          key: r.saleId,
        }))}
        empty="No sales yet."
        testId="my-sales-table"
        columns={[
          {
            key: "saleCode",
            header: "Bill",
            render: (r) => <span>{r.saleCode || r.billNumber}</span>,
          },
          {
            key: "createdAt",
            header: "Created",
            render: (r) => dateTime(r.createdAt || r.saleDateTime),
          },
          {
            key: "items",
            header: "Items",
            render: (r) => (r.items || []).length,
          },
          {
            key: "grandTotal",
            header: "Total",
            render: (r) => currency(r.grandTotal ?? r.grandTotalAmount),
          },
          {
            key: "status",
            header: "Status",
            render: (r) => <StatusBadge value={r.status} />,
          },
          {
            key: "actions",
            header: "",
            render: (r) => (
              <div className="flex items-center justify-start gap-2">
                <button
                  type="button"
                  onClick={() => openSummary(r)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-navy-900 shadow-sm transition hover:bg-amber-600"
                >
                  <ReceiptText className="h-3.5 w-3.5" />
                  Summary
                </button>

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `${saleDetailPath(r.saleId)}${
                        currentQueryString ? `?${currentQueryString}` : ""
                      }`
                    )
                  }
                  className="inline-flex items-center gap-1.5 rounded-lg bg-navy-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-navy-800"
                >
                  <Eye className="h-3.5 w-3.5" />
                  More
                </button>
              </div>
            ),
          },
        ]}
      />

      {summarySale && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
              <div>
                <h3 className="font-display text-xl font-bold text-slate-900">
                  Sold Items Summary
                </h3>

                <p className="text-sm text-slate-500 mt-1">
                  Bill {summarySale.saleCode || summarySale.billNumber} ·{" "}
                  {dateTime(summarySale.createdAt || summarySale.saleDateTime)}
                </p>

                <p className="text-sm text-slate-500">
                  Cashier:{" "}
                  {summarySale.cashierName ||
                    summarySale.cashierFullName ||
                    summarySale.cashierUsername}
                </p>
              </div>

              <button
                type="button"
                onClick={closeSummary}
                className="h-9 w-9 rounded-lg border border-slate-200 grid place-items-center text-slate-500 hover:bg-slate-50 hover:text-rose-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[calc(90vh-170px)]">
              {summaryLoading ? (
                <div className="grid place-items-center py-10 text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : summaryError ? (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  {summaryError}
                </div>
              ) : (
                <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left">
                        <th className="py-2 text-xs font-semibold uppercase text-slate-500">
                          Item
                        </th>

                        <th className="py-2 text-xs font-semibold uppercase text-slate-500">
                          Type
                        </th>

                        <th className="py-2 text-xs font-semibold uppercase text-slate-500 text-right">
                          Qty
                        </th>

                        <th className="py-2 text-xs font-semibold uppercase text-slate-500 text-right">
                          Unit
                        </th>

                        <th className="py-2 text-xs font-semibold uppercase text-slate-500 text-right">
                          Total
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {(summarySale.items || []).map((item) => {
                        const isLiquor = isLiquorSaleItem(item);
                        const isMeasuredSale =
                          isLiquor &&
                          item.bottleSaleMode &&
                          item.bottleSaleMode !==
                            BOTTLE_SALE_MODES.FULL_BOTTLE;

                        return (
                          <tr
                            key={item.saleItemId}
                            className="border-b border-slate-100"
                          >
                            <td className="py-2">
                              <div className="font-medium text-slate-900">
                                {item.itemName}
                              </div>

                              {isLiquor && isMeasuredSale && (
                                <div className="text-xs text-amber-700 mt-0.5">
                                  {item.measureMl} ml serving
                                </div>
                              )}

                              {isLiquor && !isMeasuredSale && (
                                <div className="text-xs text-slate-500 mt-0.5">
                                  Full bottle
                                </div>
                              )}
                            </td>

                            <td className="py-2">
                              <StatusBadge value={getSaleItemTypeLabel(item, BOTTLE_SALE_MODES)} />
                            </td>

                            <td className="py-2 text-right">
                              {item.quantity}
                            </td>

                            <td className="py-2 text-right">
                              {currency(item.unitPrice)}
                            </td>

                            <td className="py-2 text-right font-medium">
                              {currency(item.totalPrice)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-slate-200 p-4">
                      <h4 className="font-display font-semibold text-slate-900 mb-2">
                        Payments
                      </h4>

                      <div className="space-y-2 text-sm">
                        {(summarySale.payments || []).map((payment) => (
                          <div
                            key={payment.paymentId}
                            className="flex items-center justify-between"
                          >
                            <span className="text-slate-600">
                              <StatusBadge value={payment.paymentMethod} />

                              {payment.referenceNumber
                                ? ` · ${payment.referenceNumber}`
                                : ""}
                            </span>

                            <span className="font-medium">
                              {currency(payment.amount)}
                            </span>
                          </div>
                        ))}

                        {(summarySale.payments || []).length === 0 && (
                          <div className="text-slate-400">
                            No payment records.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <h4 className="font-display font-semibold text-slate-900 mb-2">
                        Total Summary
                      </h4>

                      <SummaryRow
                        label="Sub total"
                        value={currency(
                          summarySale.subTotal ?? summarySale.subtotalAmount
                        )}
                      />

                      <SummaryRow
                        label="Discount"
                        value={`- ${currency(summarySale.discountAmount)}`}
                      />

                      <SummaryRow
                        label="Service"
                        value={currency(summarySale.serviceChargeAmount)}
                      />

                      <SummaryRow
                        label="Tax"
                        value={currency(summarySale.taxAmount)}
                      />

                      <div className="mt-2 pt-2 border-t border-slate-200 flex justify-between">
                        <span className="font-display font-semibold">
                          Grand total
                        </span>

                        <span className="font-display font-bold text-lg text-navy-900">
                          {currency(
                            summarySale.grandTotal ??
                              summarySale.grandTotalAmount
                          )}
                        </span>
                      </div>

                      <div className="mt-3">
                        <StatusBadge value={summarySale.status} />
                      </div>
                    </div>
                  </div>

                  {isSaleSplitPayment(summarySale) && (
                    <PaymentDivisionCard sale={summarySale} />
                  )}
                </>
              )}
            </div>

            <div className="border-t border-slate-200 p-4 flex justify-end">
              <button
                type="button"
                onClick={closeSummary}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const PaymentDivisionCard = ({ sale }) => {
  const division = calculateSalePaymentDivision(sale);

  return (
    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-display font-semibold text-slate-900">
            Payment Division
          </h4>
          <p className="text-xs text-amber-800 mt-1">
            Shown because this bill has both cash and card payments.
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-white bg-white p-3 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Cash payment
          </div>
          <SummaryRow label="Bar cash" value={currency(division.barCash)} />
          <SummaryRow
            label="Restaurant cash"
            value={currency(division.restaurantCash)}
          />
        </div>

        <div className="rounded-lg border border-white bg-white p-3 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">
            Card payment
          </div>
          <SummaryRow label="Bar card" value={currency(division.barCard)} />
          <SummaryRow
            label="Restaurant card"
            value={currency(division.restaurantCard)}
          />
        </div>
      </div>

      <details className="mt-3 rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700">
        <summary className="cursor-pointer list-none font-semibold text-navy-800 inline-flex items-center gap-1.5">
          <Info className="h-3.5 w-3.5" />
          Show exact calculation
        </summary>

        <div className="mt-3 space-y-1.5">
          <SummaryRow label="Bottle / Bar total" value={currency(division.bottleTotal)} />
          <SummaryRow
            label="Restaurant total"
            value={currency(division.restaurantTotal)}
          />
          <SummaryRow label="Subtotal" value={currency(division.subtotal)} />
          <SummaryRow
            label="Bar ratio"
            value={`${division.bottleTotal.toLocaleString()} / ${division.subtotal.toLocaleString()} = ${formatRatioPercent(division.barRatio)}`}
          />
          <SummaryRow
            label="Restaurant ratio"
            value={`${division.restaurantTotal.toLocaleString()} / ${division.subtotal.toLocaleString()} = ${formatRatioPercent(division.restaurantRatio)}`}
          />
          <SummaryRow label="Cash received" value={currency(division.cashAmount)} />
          <SummaryRow
            label="Bar cash"
            value={`${currency(division.cashAmount)} × ${formatRatioPercent(division.barRatio)} = ${currency(division.barCash)}`}
          />
          <SummaryRow
            label="Restaurant cash"
            value={`${currency(division.cashAmount)} × ${formatRatioPercent(division.restaurantRatio)} = ${currency(division.restaurantCash)}`}
          />
          <SummaryRow label="Card amount" value={currency(division.cardAmount)} />
          <SummaryRow
            label="Bar card"
            value={`${currency(division.cardAmount)} × ${formatRatioPercent(division.barRatio)} = ${currency(division.barCard)}`}
          />
          <SummaryRow
            label="Restaurant card"
            value={`${currency(division.cardAmount)} × ${formatRatioPercent(division.restaurantRatio)} = ${currency(division.restaurantCard)}`}
          />
        </div>
      </details>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between py-1 text-sm text-slate-700">
    <span>{label}</span>
    <span>{value}</span>
  </div>
);
